// frontend/src/components/ProfileKit.js
//
// 'Your AI profile kit' on the handle name-generator pages (Growth round, T23;
// mockup .jarvis/design/AIKit.dc.html). Opens under the 'Nice pick' panel once
// a name has been copied.
//
// One call to POST /api/public/ai/profile-kit makes 3 bios, captions for the
// first 3 posts and a hashtag set. Logged out, the server sends ONLY bio 1 and
// the counts of the rest, so the blurred block below is placeholder text, not
// the real captions: there is nothing to un-blur in the browser. 'Unlock it
// free' goes to sign-up with the name, the page and the kit id; the server
// attaches the stored kit to the new account (Link in Bio shows it after).
// Logged in, the server spends 1 AI credit and sends the whole kit.
import React, { useEffect, useRef, useState } from 'react'

import { claimNameFor, isClaimable, signupHref } from '../utils/nameClaim'
import { fetchProfileKit, readLastKit, saveLastKit, vibePlatformFor } from '../utils/publicAi'
import { trackFunnel } from '../utils/funnel'
import { AI, Sparkle, FreeTryCard } from './VibeNames'

const BOX = {
  border: '1px solid #e5e7eb', borderRadius: 10, padding: 16,
  display: 'flex', flexDirection: 'column', gap: 8,
}
const KICKER = { fontSize: 13, fontWeight: 700, color: '#6c757d', letterSpacing: 0.4 }
const COPY_BTN = {
  height: 32, padding: '0 12px', border: '1px solid #6c757d', borderRadius: 6, background: '#fff',
  fontWeight: 600, fontSize: 13, color: '#495057', flex: 'none',
}
const AI_BTN = {
  height: 44, padding: '0 20px', borderRadius: 8, background: AI, color: '#fff', fontWeight: 700,
  fontSize: 15, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, border: 0,
}

// Stand-in text under the blur. The real captions never reach a logged-out browser.
const placeholder = name => [
  `Post 1: hello from @${name}! here's what this little corner of the internet is all about`,
  'Post 2: three things that made this week better, and why I wanted to share them here',
  "Post 3: a peek behind the scenes, plus what's coming next on this account",
]
const PLACEHOLDER_TAGS = '#newaccount #dailyinspo #creatorlife #aestheticfeed #followalong #smallcreator'

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`

function CopyButton ({ text, label, done, onCopy }) {
  return (
    <button type='button' style={COPY_BTN} onClick={() => onCopy(text, label)}>
      {done === label ? 'Copied' : label}
    </button>
  )
}

/** 'Captions for your first 3 posts, 2 more bios and your hashtag set.' */
function lockedLine (locked) {
  const parts = []
  if (locked.captions) parts.push(`captions for your first ${plural(locked.captions, 'post', 'posts')}`)
  if (locked.bioVariants) parts.push(plural(locked.bioVariants, 'more bio', 'more bios'))
  if (locked.hashtags) parts.push('your hashtag set')
  if (!parts.length) return 'Free with a Greatly Social account.'
  const list = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0]
  return `${list.charAt(0).toUpperCase()}${list.slice(1)}. Free with a Greatly Social account.`
}

const errorText = r => {
  if (r.status === 402) return 'You’ve used all your AI credits for this cycle. Upgrade your plan for more.'
  if (r.message && r.status !== 0) return r.message
  return 'Could not make your profile kit right now. Try again in a moment.'
}

export default function ProfileKit ({ cfg, name, style, styleLabel, vibe, status }) {
  const [kit, setKit] = useState(() => readLastKit(name, vibePlatformFor(cfg)))
  const [seconds, setSeconds] = useState(null)
  const [loading, setLoading] = useState(false)
  const [card, setCard] = useState('')        // '' | 'free_try_used' | 'daily_cap'
  const [error, setError] = useState('')
  const [copied, setCopied] = useState('')
  const copiedTimer = useRef(null)
  useEffect(() => () => clearTimeout(copiedTimer.current), [])

  const username = claimNameFor(name)
  const taken = status && status !== 'checking' && !status.available
  const willClaim = isClaimable(username) && !taken
  const styleWords = String(styleLabel || style || '').toLowerCase()

  const make = async () => {
    setError('')
    setCard('')
    setLoading(true)
    const started = Date.now()
    const r = await fetchProfileKit({ name, style, platform: vibePlatformFor(cfg), vibe })
    setLoading(false)
    if (r.ok) {
      setSeconds(Math.max(1, Math.round((Date.now() - started) / 1000)))
      setKit(r.data)
      saveLastKit(r.data)
      return
    }
    if (r.status === 429 && (r.error === 'free_try_used' || r.error === 'daily_cap')) { setCard(r.error); return }
    setError(errorText(r))
  }

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(label)
      clearTimeout(copiedTimer.current)
      copiedTimer.current = setTimeout(() => setCopied(''), 1400)
    } catch { /* clipboard blocked: the text is selectable */ }
  }

  const unlockHref = signupHref({ claim: willClaim ? username : '', from: cfg.slug, kit: kit && kit.kitId })

  const header = (
    <div className='d-flex align-items-center' style={{ gap: 10 }}>
      <Sparkle size={24} twin color={AI} />
      <div>
        <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>Your AI profile kit for @{name}</h2>
        <div style={{ fontSize: 15, color: '#495057' }}>
          {kit
            ? <>{seconds ? `Made in ${plural(seconds, 'second', 'seconds')}, in` : 'Made in'} your {styleWords} style.</>
            : <>A matching bio, captions for your first 3 posts and a hashtag set, in your {styleWords} style.</>}
        </div>
      </div>
    </div>
  )

  const bios = kit ? (kit.bio.variants || [kit.bio.text]) : []
  const totalBios = kit ? (kit.locked ? 1 + kit.locked.bioVariants : bios.length) : 0

  return (
    <section data-testid='profile-kit' aria-label={`Your AI profile kit for @${name}`} className='mt-4'
             style={{ background: '#fff', border: `2px solid ${AI}`, borderRadius: 14, padding: 26,
                      display: 'flex', flexDirection: 'column', gap: 18 }}>
      {header}

      {!kit && (
        <div className='d-flex align-items-center flex-wrap' style={{ gap: 12 }}>
          <button type='button' onClick={make} disabled={loading} aria-busy={loading} style={AI_BTN}>
            <Sparkle size={16} />
            {loading ? 'Making your kit…' : 'Make my profile kit'}
          </button>
          <span style={{ fontSize: 14, color: '#6c757d' }}>One free AI kit a day, no account needed.</span>
        </div>
      )}

      {error && <p role='alert' className='text-danger small mb-0'>{error}</p>}
      <div role='status' aria-live='polite'>
        {card && <FreeTryCard kind={card} href={signupHref({ claim: willClaim ? username : '', from: cfg.slug })} />}
      </div>

      {kit && bios.map((text, i) => (
        <div key={i} style={BOX} data-testid='kit-bio'>
          <div className='d-flex align-items-center justify-content-between' style={{ gap: 8 }}>
            <span style={KICKER}>
              BIO · {i + 1} OF {totalBios}{kit.locked ? ' · YOURS FREE' : ''}
            </span>
            <CopyButton text={text} label={bios.length > 1 ? `Copy bio ${i + 1}` : 'Copy bio'} done={copied} onCopy={copy} />
          </div>
          <div style={{ fontSize: 17, lineHeight: 1.5, whiteSpace: 'pre-line' }}>{text}</div>
        </div>
      ))}

      {kit && kit.locked && (
        <div data-testid='kit-locked' style={{ ...BOX, position: 'relative', gap: 12, overflow: 'hidden' }}>
          <div style={KICKER}>YOUR FIRST 3 POSTS · CAPTIONS AND HASHTAGS</div>
          <div aria-hidden='true' data-testid='kit-blurred'
               style={{ display: 'flex', flexDirection: 'column', gap: 12, filter: 'blur(5px)', userSelect: 'none', pointerEvents: 'none' }}>
            {placeholder(name).map(t => <div key={t} style={{ fontSize: 16 }}>{t}</div>)}
            <div style={{ fontSize: 15, color: '#6c757d' }}>{PLACEHOLDER_TAGS}</div>
          </div>
          <div style={{ position: 'absolute', left: 0, right: 0, top: 40, bottom: 0, display: 'flex',
                        alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.55)', padding: 12 }}>
            <div style={{ background: '#fff', border: '1px solid #c7d2fe', borderRadius: 12, padding: '18px 22px',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
                          boxShadow: '0 10px 30px rgba(67,56,202,0.18)', maxWidth: 440, textAlign: 'center' }}>
              <div style={{ fontWeight: 800, fontSize: 18 }}>Unlock your full profile kit</div>
              <div style={{ fontSize: 15, color: '#495057' }}>{lockedLine(kit.locked)}</div>
              <a href={unlockHref} onClick={() => trackFunnel('kit_unlock_click', cfg.slug)} style={AI_BTN}>Unlock it free</a>
              {willClaim && (
                <div style={{ fontSize: 13, color: '#6c757d' }}>We'll also save @{username} as your link in bio.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {kit && !kit.locked && (
        <>
          <div style={BOX} data-testid='kit-captions'>
            <div style={KICKER}>YOUR FIRST {kit.captions.length} POSTS · CAPTIONS</div>
            {kit.captions.map((c, i) => (
              <div key={i} className='d-flex align-items-start justify-content-between' style={{ gap: 12 }}>
                <div style={{ fontSize: 16, whiteSpace: 'pre-line' }}><strong>Post {i + 1}:</strong> {c}</div>
                <CopyButton text={c} label={`Copy post ${i + 1}`} done={copied} onCopy={copy} />
              </div>
            ))}
          </div>
          <div style={BOX} data-testid='kit-hashtags'>
            <div className='d-flex align-items-center justify-content-between' style={{ gap: 8 }}>
              <span style={KICKER}>YOUR HASHTAG SET · {kit.hashtags.length}</span>
              <CopyButton text={kit.hashtags.join(' ')} label='Copy hashtags' done={copied} onCopy={copy} />
            </div>
            <div style={{ fontSize: 15, color: '#495057' }}>{kit.hashtags.join(' ')}</div>
          </div>
        </>
      )}
    </section>
  )
}
